import { toast } from 'sonner';
import { Stack } from '@mui/material';
import React, { useMemo } from 'react';
import { collection, doc, Timestamp, writeBatch } from '@firebase/firestore';

import type {
  ActionedBy,
  CustomStep,
  CustomStepType,
  HealthCategoryId,
  StatusActivity,
} from '@shared/patient-intake-status';

import { db } from '../../../../../firebase-config.js';
import { useAuth } from '../../../../../hooks/index.js';
import { sortCustomSteps } from '../../utils';
import { usePatientIntakeStatusContext, useStepEdit } from '../../hooks';
import { CompleteStepDialog } from '../../CompleteStepDialog';
import { EmptyStepCard } from './EmptyStepCard.tsx';
import { MessageAction } from './MessageAction.tsx';
import { UploadDocumentAction } from './UploadDocumentAction.tsx';
import { DischargeLetterAction } from './DischargeLetterAction.tsx';
import type { BaseActionPromptProps } from './CustomSteps.types.ts';

const throwNever = (type: never) => {
  throw new Error(`Unknown custom step type: ${type}`);
};

type ToggleCustomStepParams = {
  step: CustomStep[CustomStepType];
  patientId: string;
  isCurrentlyDisabled: boolean;
  actionedBy: ActionedBy;
};

const toggleCustomStep = async ({ patientId, step, isCurrentlyDisabled, actionedBy }: ToggleCustomStepParams) => {
  const docRef = doc(db, 'patients', patientId, 'intakeStatus', step.id);
  const statusActivityRef = doc(collection(db, 'patients', patientId, 'intakeStatusActivity'));

  const batch = writeBatch(db);

  batch.update(docRef, {
    isDisabled: !isCurrentlyDisabled,
  });

  batch.set(statusActivityRef, {
    actionedBy,
    stepId: step.id,
    stepSnapshot: step,
    action: 'TOGGLE_DISABLED',
    actionedAt: Timestamp.now(),
    healthCategoryId: step.healthCategoryId,
    metadata: {
      isDisabled: !isCurrentlyDisabled,
    },
  } satisfies StatusActivity<'TOGGLE_DISABLED'>);

  await batch.commit();
};

type DeleteCustomStepParams = {
  patientId: string;
  step: CustomStep[CustomStepType];
  actionedBy: ActionedBy;
};

const deleteCustomStep = async ({ patientId, step, actionedBy }: DeleteCustomStepParams) => {
  const docRef = doc(db, 'patients', patientId, 'intakeStatus', step.id);
  const statusActivityRef = doc(collection(db, 'patients', patientId, 'intakeStatusActivity'));

  const batch = writeBatch(db);

  batch.update(docRef, {
    isDeleted: true,
    deletedAt: Timestamp.now(),
  });

  batch.set(statusActivityRef, {
    actionedBy,
    stepId: step.id,
    stepSnapshot: step,
    action: 'DELETE_STEP',
    actionedAt: Timestamp.now(),
    healthCategoryId: step.healthCategoryId,
    metadata: {},
  } satisfies StatusActivity<'DELETE_STEP'>);

  await batch.commit();
};

interface CustomStepComponentProps extends BaseActionPromptProps {
  step: CustomStep[CustomStepType];
  activeHealthCategoryId: string;
}

const CustomStepComponent = ({ step, activeHealthCategoryId, ...props }: CustomStepComponentProps) => {
  if (step.type === 'DOCUMENT_UPLOAD') {
    return <UploadDocumentAction step={step} activeHealthCategoryId={activeHealthCategoryId} {...props} />;
  }

  if (step.type === 'MESSAGE') {
    return <MessageAction step={step} activeHealthCategoryId={activeHealthCategoryId} {...props} />;
  }

  if (step.type === 'DISCHARGE_LETTER_UPLOAD') {
    return <DischargeLetterAction step={step} activeHealthCategoryId={activeHealthCategoryId} {...props} />;
  }

  return throwNever(step);
};

type CustomStepsProps = {
  activeHealthCategoryId: HealthCategoryId;
};

export const CustomSteps: React.FC<CustomStepsProps> = ({ activeHealthCategoryId }) => {
  const { user, userType } = useAuth();
  const { editingStep, setEditingStep } = useStepEdit();
  const { customSteps, patientId } = usePatientIntakeStatusContext();

  const handleOnEdit = ({ healthCategoryId, stepId }: { healthCategoryId: HealthCategoryId; stepId: string }) => {
    const step = customSteps?.[healthCategoryId]?.[stepId];

    if (!step) {
      return;
    }

    setEditingStep(step);
  };

  const handleOnDelete = async ({ stepId }: { stepId: string }) => {
    const step = customSteps?.[activeHealthCategoryId]?.[stepId];

    if (!step) {
      toast.error('Step not found');

      return;
    }

    if (!user || !userType || !user.uid || !user.email) {
      toast.error('Please login to continue');

      return;
    }

    const actionedBy = {
      id: user.uid,
      email: user.email,
      name: null,
      userType,
    } satisfies ActionedBy;

    return toast
      .promise(deleteCustomStep({ patientId, step, actionedBy }), {
        loading: 'Deleting step...',
        success: 'Step deleted',
        error: 'Failed to delete step',
      })
      .unwrap();
  };

  const handleOnHide = async ({
    step,
    isCurrentlyDisabled,
  }: {
    step: CustomStep[CustomStepType];
    isCurrentlyDisabled: boolean;
  }) => {
    if (!user || !userType || !user.uid || !user.email) {
      toast.error('Please login to continue');

      return;
    }

    const actionedBy = {
      id: user.uid,
      email: user.email,
      name: null,
      userType,
    } satisfies ActionedBy;

    await toast
      .promise(toggleCustomStep({ patientId, step, isCurrentlyDisabled, actionedBy }), {
        loading: 'Updating step visibility...',
        success: 'Step visibility updated',
        error: 'Failed to update step visibility',
      })
      .unwrap();
  };

  const sortedSteps = useMemo(() => {
    if (!customSteps?.[activeHealthCategoryId]) {
      return [];
    }

    return Object.values(customSteps[activeHealthCategoryId]).sort(sortCustomSteps());
  }, [customSteps, activeHealthCategoryId]);

  if (!customSteps) {
    return null;
  }

  const shouldShowEditingStep =
    !!editingStep &&
    editingStep.step.stepType === 'CUSTOM_STEP' &&
    editingStep.step.healthCategoryId === activeHealthCategoryId;

  return (
    <Stack key={'custom-steps'} sx={{ position: 'relative', gap: 1 }}>
      <EmptyStepCard />

      {sortedSteps.map((step) => (
        <CustomStepComponent
          key={step.id}
          step={step}
          activeHealthCategoryId={activeHealthCategoryId}
          onEdit={() => handleOnEdit({ healthCategoryId: activeHealthCategoryId, stepId: step.id })}
          onDelete={() => handleOnDelete({ stepId: step.id })}
          onToggleDisabled={async () => await handleOnHide({ step, isCurrentlyDisabled: !!step.isDisabled })}
        />
      ))}

      {shouldShowEditingStep && (
        <CompleteStepDialog
          open={shouldShowEditingStep}
          onClose={() => setEditingStep(null)}
          onSuccess={() => setEditingStep(null)}
          {...editingStep}
        />
      )}
    </Stack>
  );
};
